import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { WikiMarkdown } from './WikiMarkdown.js';
import type {
  KnowledgeWikiCodeBlock,
  KnowledgeWikiDetail,
  KnowledgeWikiStructured,
  KnowledgeWikiUpdateInput,
} from '../../../types/knowledge-wiki.js';

export interface DocumentEditorProps {
  /** 編集対象。読み込み済みの詳細をそのまま初期値にする */
  detail: KnowledgeWikiDetail;
  /** 保存中かどうか */
  isSaving: boolean;
  /** 保存に失敗した場合のメッセージ */
  errorMessage?: string | null;
  /** 保存要求 */
  onSave: (input: KnowledgeWikiUpdateInput) => void;
  /** 編集の取りやめ */
  onCancel: () => void;
}

/**
 * Markdown から先頭のコードフェンスを読み、言語と中身を取り出す。
 *
 * `markdown` が正データなので、`language` / `code` は保存時にここで導出し直す。
 * 手で両方を編集させると 3 つの値がすぐ食い違う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 編集された Markdown から言語とコード本体を導出して CodeBlock を組み立てる
 * @graph-connects none
 */
export function toCodeBlock(markdown: string): KnowledgeWikiCodeBlock | null {
  const trimmed = markdown.trim();
  if (trimmed === '') return null;
  const fence = /```([\w+-]*)\n([\s\S]*?)```/.exec(trimmed);
  return {
    language: fence?.[1] ?? '',
    code: fence?.[2]?.replace(/\n$/, '') ?? trimmed,
    markdown: trimmed,
  };
}

/** 空文字を null に寄せる。未記入と空文字を同じ状態にする。 @graph-connects none */
function orNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

/** 1 項目分の編集欄。書きながら描画結果を確かめられるようにする。 @graph-connects none */
function Field({
  label,
  value,
  rows,
  help,
  onChange,
}: {
  label: string;
  value: string;
  rows: number;
  help?: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  return (
    <section>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{label}</h3>
        <button
          type="button"
          onClick={() => {
            setShowPreview((prev) => !prev);
          }}
          aria-pressed={showPreview}
          className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        >
          {showPreview ? t('knowledgeWiki.edit.hidePreview') : t('knowledgeWiki.edit.showPreview')}
        </button>
      </div>
      {help !== undefined && <p className="mb-1 text-xs text-[var(--color-text-muted)]">{help}</p>}
      <Textarea
        value={value}
        rows={rows}
        aria-label={label}
        className="text-sm"
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      {showPreview && (
        <div className="mt-2 rounded-lg border border-dashed border-[var(--color-border)] p-3">
          <WikiMarkdown markdown={value} />
        </div>
      )}
    </section>
  );
}

/**
 * 項目別の編集フォーム。
 *
 * 一枚の Markdown を丸ごと編集させるのではなく、理由 / 悪い例 / 良い例のような
 * 項目ごとに欄を分ける。どこに何を書くのかが欄の存在で示されるので、必須項目の
 * 書き漏らしが起きにくい。例はコードとは限らない（手順や設計の説明でもよい）ため、
 * Markdown をそのまま書ける欄にし、隣にプレビューを出して結果を確かめられるようにする。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business wiki 記事を項目単位で編集し、変更理由付きで保存する
 * @graph-connects none
 */
export function DocumentEditor({
  detail,
  isSaving,
  errorMessage,
  onSave,
  onCancel,
}: DocumentEditorProps) {
  const { t } = useTranslation();
  const structured = detail.structured;

  const [title, setTitle] = useState(detail.title);
  const [description, setDescription] = useState(detail.description ?? '');
  const [note, setNote] = useState('');

  const isAntiPattern = structured?.kind === 'anti-pattern';
  const [reason, setReason] = useState(
    structured?.kind === 'anti-pattern' ? (structured.reason ?? '') : '',
  );
  const [badExample, setBadExample] = useState(
    structured?.kind === 'anti-pattern' ? (structured.badExample?.markdown ?? '') : '',
  );
  const [goodExample, setGoodExample] = useState(
    structured?.kind === 'anti-pattern' ? (structured.goodExample?.markdown ?? '') : '',
  );
  const [overview, setOverview] = useState(
    structured?.kind === 'guideline' ? (structured.overview ?? '') : '',
  );
  const [codeExample, setCodeExample] = useState(
    structured?.kind === 'guideline' ? (structured.codeExample?.markdown ?? '') : '',
  );
  const [notes, setNotes] = useState(structured?.notes ?? '');

  const buildStructured = (): KnowledgeWikiStructured =>
    isAntiPattern
      ? {
          kind: 'anti-pattern',
          reason: orNull(reason),
          badExample: toCodeBlock(badExample),
          goodExample: toCodeBlock(goodExample),
          notes: orNull(notes),
        }
      : {
          kind: 'guideline',
          overview: orNull(overview),
          codeExample: toCodeBlock(codeExample),
          notes: orNull(notes),
        };

  // アンチパターンは悪い例と良い例が揃っていないと保存側で拒否されるため、
  // 押せてから失敗するのではなく、押す前に条件を示す。
  const missingRequired = isAntiPattern
    ? [
        toCodeBlock(badExample) === null ? t('knowledgeWiki.field.badExample') : null,
        toCodeBlock(goodExample) === null ? t('knowledgeWiki.field.goodExample') : null,
      ].filter((label): label is string => label !== null)
    : [];
  const canSave =
    title.trim() !== '' && note.trim() !== '' && missingRequired.length === 0 && !isSaving;

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        onSave({
          localeCode: detail.localeCode,
          title: title.trim(),
          description: orNull(description),
          structured: buildStructured(),
          note: note.trim(),
        });
      }}
    >
      <Input
        label={t('knowledgeWiki.edit.titleLabel')}
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
      />
      <Input
        label={t('knowledgeWiki.edit.descriptionLabel')}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />

      {isAntiPattern ? (
        <>
          <Field
            label={t('knowledgeWiki.field.reason')}
            value={reason}
            rows={4}
            onChange={setReason}
          />
          <Field
            label={t('knowledgeWiki.field.badExample')}
            value={badExample}
            rows={10}
            help={t('knowledgeWiki.edit.exampleHelp')}
            onChange={setBadExample}
          />
          <Field
            label={t('knowledgeWiki.field.goodExample')}
            value={goodExample}
            rows={10}
            help={t('knowledgeWiki.edit.exampleHelp')}
            onChange={setGoodExample}
          />
        </>
      ) : (
        <>
          <Field
            label={t('knowledgeWiki.field.overview')}
            value={overview}
            rows={6}
            onChange={setOverview}
          />
          <Field
            label={t('knowledgeWiki.field.codeExample')}
            value={codeExample}
            rows={10}
            help={t('knowledgeWiki.edit.exampleHelp')}
            onChange={setCodeExample}
          />
        </>
      )}
      <Field label={t('knowledgeWiki.field.notes')} value={notes} rows={4} onChange={setNotes} />

      <Input
        label={t('knowledgeWiki.edit.noteLabel')}
        helperText={t('knowledgeWiki.edit.noteHelp')}
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
        }}
      />

      {missingRequired.length > 0 && (
        <p className="text-sm text-[var(--color-warning)]">
          {t('knowledgeWiki.edit.missingRequired', { fields: missingRequired.join(' / ') })}
        </p>
      )}
      {errorMessage != null && errorMessage !== '' && (
        <p className="text-sm text-[var(--color-error)]">{errorMessage}</p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={!canSave}>
          {isSaving ? t('knowledgeWiki.edit.saving') : t('knowledgeWiki.edit.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
          {t('knowledgeWiki.edit.cancel')}
        </Button>
      </div>
    </form>
  );
}
